import { useNavigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { WizardLayout } from '../../../components/wizard/WizardLayout';
import { de } from '../../../lib/i18n/de';
import { getStep } from '../../../components/hub/dimensions';
import {
  RESOURCES_SUB_STEPS,
  useWizardStore,
  type ResourcesSubStepKey,
} from '../../../lib/store/wizard';
import { useWizardAudience } from '../../../components/wizard/WizardAudienceContext';
import { ResourcesSubStepNav } from './ResourcesSubStepNav';
import { ResourcesLiveScore } from './ResourcesLiveScore';
import { BwaInputStep } from './BwaInputStep';
import { StundensatzStep } from './StundensatzStep';
import { LiquiditaetStep } from './LiquiditaetStep';
import { BgStep } from './BgStep';
import { LueckeStep } from './LueckeStep';

/**
 * Modul 05 — Ressourcen & Finanzen.
 *
 * Strukturierter Sub-Wizard mit fünf Sub-Steps:
 *   4.1 BWA-Eckdaten      → BwaInputStep
 *   4.2 Stundensatz       → StundensatzStep
 *   4.3 Liquidität        → LiquiditaetStep
 *   4.4 BG                → BgStep
 *   4.5 Tragfähigkeitslücke → LueckeStep
 *
 * Gemeinsamer Editorial-Header (Modul-Nummer, Titel, Subtitle), darunter eine
 * segmentierte Sub-Step-Navigation, dann der jeweils aktive Sub-Step. Am Fuß
 * der Live-Score-Footer und die „Zurück / Weiter"-Navigation, die zwischen
 * den Sub-Steps blättert (am letzten Sub-Step wird daraus „Modul abschließen").
 */
export function ResourcesStep() {
  const { id: caseId = 'demo' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const audience = useWizardAudience();
  // Audience-abhängige Home-Basis: Mandanten dürfen NICHT in den Berater-Hub
  // (/case/:id/hub) navigieren — sonst landen sie in einer fremden Rolle.
  const homeBase = audience === 'mandant' ? `/mandant/case/${caseId}` : `/case/${caseId}/hub`;
  const step = getStep('resources');

  const ensureCase = useWizardStore((s) => s.ensureCase);
  const resources = useWizardStore((s) => s.cases[caseId]?.resources);
  const lastSavedAt = useWizardStore((s) => s.cases[caseId]?.lastSavedAt) ?? null;

  const setSubStep = useWizardStore((s) => s.setResourcesSubStep);
  const setField = useWizardStore((s) => s.setResourcesField);
  const setKdu = useWizardStore((s) => s.setResourcesKdu);
  const addBgMember = useWizardStore((s) => s.addBgMember);
  const updateBgMember = useWizardStore((s) => s.updateBgMember);
  const removeBgMember = useWizardStore((s) => s.removeBgMember);
  const markComplete = useWizardStore((s) => s.markDimensionComplete);

  useEffect(() => {
    ensureCase(caseId);
  }, [caseId, ensureCase]);

  if (!resources) {
    // Erster Render bevor ensureCase greift — kurze Lücke, kein Crash.
    return (
      <WizardLayout>
        <p className="font-serif italic text-stone-500">Wird geladen …</p>
      </WizardLayout>
    );
  }

  const active = resources.activeSubStep;
  const idx = RESOURCES_SUB_STEPS.indexOf(active);
  const isFirstSub = idx === 0;
  const isLastSub = idx === RESOURCES_SUB_STEPS.length - 1;

  const handlePrev = () => {
    if (!isFirstSub) {
      const prev = RESOURCES_SUB_STEPS[idx - 1] as ResourcesSubStepKey;
      setSubStep(caseId, prev);
      return;
    }
    void navigate(homeBase);
  };

  const handleNext = () => {
    if (isLastSub) {
      markComplete(caseId, 'resources');
      void navigate(homeBase);
      return;
    }
    const next = RESOURCES_SUB_STEPS[idx + 1] as ResourcesSubStepKey;
    setSubStep(caseId, next);
  };

  return (
    <WizardLayout>
      <section
        aria-labelledby="resources-step-title"
        className="rounded-sm border border-stone-200 bg-white"
      >
        <header className="relative flex flex-col gap-3 border-b border-stone-200 px-6 pb-8 pt-10 md:px-10 md:pb-10 md:pt-14">
          <div
            aria-hidden="true"
            className="absolute right-6 top-6 h-12 w-12 rounded-sm md:right-10 md:top-10 md:h-16 md:w-16"
            style={{ backgroundColor: step.accentHex, opacity: 0.4 }}
          />
          <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
            Modul {step.nummer}
          </p>
          <h1
            id="resources-step-title"
            className="font-serif text-3xl font-black leading-none tracking-tight text-stone-900 md:text-5xl"
          >
            <span className="block">RESSOURCEN &amp;</span>
            <span className="block">FINANZEN.</span>
          </h1>
          <p className="font-serif text-base italic text-stone-500 md:text-lg">{step.subtitle}</p>
        </header>

        <div className="px-6 py-8 md:px-10 md:py-10">
          <ResourcesSubStepNav
            active={active}
            onSelect={(key) => {
              setSubStep(caseId, key);
            }}
          />

          <div className="mt-8" data-testid={`resources-substep-${active}`}>
            {active === 'bwa' && (
              <BwaInputStep
                resources={resources}
                onRevenueChange={(v) => {
                  setField(caseId, 'revenueCents', v);
                }}
                onCostsChange={(v) => {
                  setField(caseId, 'costsCents', v);
                }}
              />
            )}
            {active === 'rate' && (
              <StundensatzStep
                resources={resources}
                onHoursChange={(v) => {
                  setField(caseId, 'hoursPerMonth', v);
                }}
              />
            )}
            {active === 'liquidity' && (
              <LiquiditaetStep
                resources={resources}
                onLiquidityChange={(v) => {
                  setField(caseId, 'liquidityCents', v);
                }}
                onBurnRateChange={(v) => {
                  setField(caseId, 'burnRateCents', v);
                }}
                onLiabilitiesChange={(v) => {
                  setField(caseId, 'shortTermLiabilitiesCents', v);
                }}
              />
            )}
            {active === 'bg' && (
              <BgStep
                resources={resources}
                onAddMember={(m) => {
                  addBgMember(caseId, m);
                }}
                onUpdateMember={(memberId, patch) => {
                  updateBgMember(caseId, memberId, patch);
                }}
                onRemoveMember={(memberId) => {
                  removeBgMember(caseId, memberId);
                }}
                onKduChange={(patch) => {
                  setKdu(caseId, patch);
                }}
                onBruttoChange={(v) => {
                  setField(caseId, 'bruttoErwerbseinkommenCents', v);
                }}
                onSonstigesChange={(v) => {
                  setField(caseId, 'sonstigesEinkommenCents', v);
                }}
              />
            )}
            {active === 'luecke' && <LueckeStep resources={resources} />}
          </div>

          <ResourcesLiveScore resources={resources} caseId={caseId} />
        </div>

        <footer className="flex flex-col items-stretch gap-4 border-t border-stone-200 px-6 py-6 md:flex-row md:items-center md:justify-between md:px-10">
          <SaveIndicator lastSavedAt={lastSavedAt} />
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handlePrev}
              className="inline-flex items-center justify-center rounded-sm border border-stone-200 bg-white px-5 py-3 font-sans text-xs font-medium uppercase tracking-widest text-stone-700 transition-colors hover:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
            >
              &larr; {de.wizard.previous}
            </button>
            <button
              type="button"
              onClick={handleNext}
              className="inline-flex items-center justify-center rounded-sm bg-stone-900 px-6 py-3 font-sans text-xs font-medium uppercase tracking-widest text-white transition-colors hover:bg-stone-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
            >
              {isLastSub ? de.steps.resources.subSteps.luecke.finishCta : `${de.wizard.next} →`}
            </button>
          </div>
        </footer>
      </section>
    </WizardLayout>
  );
}

function SaveIndicator({ lastSavedAt }: { lastSavedAt: string | null }) {
  if (!lastSavedAt) {
    return (
      <span className="font-sans text-xs font-medium uppercase tracking-widest text-stone-300">
        {de.wizard.autosaveExplain}
      </span>
    );
  }
  const ts = (() => {
    try {
      return new Date(lastSavedAt).toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return null;
    }
  })();
  return (
    <span className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
      {de.wizard.saved}
      {ts ? ` · ${ts}` : ''}
    </span>
  );
}
